import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { User, Lock, Eye, EyeOff, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = window.location.origin;

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const inviteToken = searchParams.get('token');
  
  const [inviteDetails, setInviteDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (inviteToken) {
      fetchInviteDetails();
    } else {
      setError('Invalid invite link');
      setLoading(false);
    }
  }, [inviteToken]);

  const fetchInviteDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/access/invite/${inviteToken}`);
      setInviteDetails(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invite');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (e) => {
    e.preventDefault();
    if (!name || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/access/invite/accept`,
        { invite_token: inviteToken, name, password },
        { withCredentials: true }
      );
      
      // Store token
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('currentWorkspaceId', response.data.workspace_id);
      
      toast.success('Welcome! Your account has been created.');
      
      // Refresh auth state and navigate
      await checkAuth();
      navigate(`/workspace/${response.data.workspace_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-space-900 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-space-900 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-16 h-16 rounded-full bg-red-400/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="text-red-400" size={32} />
          </div>
          <h1 className="text-2xl font-display font-semibold text-white mb-2">
            Invalid Invite
          </h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors"
          >
            Go to Login
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-space-900 flex items-center justify-center px-4 py-12" data-testid="accept-invite-page">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-teal-400/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-violet-400/5 rounded-full blur-[120px]" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-phosphor-400 flex items-center justify-center">
              <span className="text-space-900 font-bold text-lg">V</span>
            </div>
            <span className="font-display font-semibold text-2xl text-white">
              Voxinity<span className="text-teal-400">AI</span>
            </span>
          </Link>
        </motion.div>

        {/* Invite Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card rounded-2xl p-6 md:p-8"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-teal-400/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-teal-400" size={32} />
            </div>
            <h1 className="font-display font-semibold text-2xl text-white mb-2">
              You're Invited!
            </h1>
            <p className="text-sm text-gray-400">
              <strong className="text-white">{inviteDetails?.invited_by_name}</strong> has invited you to join
            </p>
            <p className="text-lg font-medium text-teal-400 mt-1">
              {inviteDetails?.workspace_name}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Role: <span className="text-gray-300 capitalize">{inviteDetails?.permission_level}</span>
            </p>
          </div>

          {/* Accept Form */}
          <form onSubmit={handleAccept} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Your Name</label>
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-teal-400/50 focus:outline-none focus:ring-1 focus:ring-teal-400/50 transition-all duration-200"
                  required
                  data-testid="invite-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Email</label>
              <input
                type="email"
                value={inviteDetails?.email || ''}
                disabled
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-gray-400 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Create Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  className="w-full pl-11 pr-12 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-teal-400/50 focus:outline-none focus:ring-1 focus:ring-teal-400/50 transition-all duration-200"
                  required
                  minLength={8}
                  data-testid="invite-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-space-900 font-medium hover:bg-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              data-testid="invite-accept-btn"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-space-900/30 border-t-space-900 rounded-full animate-spin" />
              ) : (
                <>
                  Accept & Join <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-500 mt-6">
            By accepting, you agree to our{" "}
            <a href="/terms" className="text-teal-400 hover:text-teal-300">Terms of Service</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
